import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ScrollText, Users, Wallet, AlertTriangle, RefreshCw, Receipt,
  FileText, Plus, ShieldAlert, Eye,
} from "lucide-react";
import api from "@/lib/api";
import DashboardShell from "@/components/layout/DashboardShell";
import { customerMenu } from "@/config/menus";
import { KpiCard } from "@/components/KpiCard";
import { Button } from "@/components/ui/button";
import { StatusPill, inr, LoadingScreen } from "@/components/common";
import { useAuth } from "@/context/AuthContext";

export default function CustomerDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [d, setD] = useState(null);

  useEffect(() => { api.get("/dashboard/customer").then((r) => setD(r.data)).catch(() => {}); }, []);

  const quick = [
    ["Request Security", FileText, "/post-requirement", true],
    ["Request Replacement", RefreshCw, "/replacements", false],
    ["Report Incident", ShieldAlert, "/incidents", false],
    ["Pay Invoice", Receipt, "/invoices", false],
  ];

  return (
    <DashboardShell menu={customerMenu} title={`Welcome, ${(user?.name || "").split(" ")[0] || "Customer"}`}>
      {!d ? <LoadingScreen /> : (
        <div className="space-y-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard icon={ScrollText} label="Active Contracts" value={d.active_contracts} accent testid="kpi-contracts" />
            <KpiCard icon={Users} label="Security Guards" value={d.security_guards} testid="kpi-guards" />
            <KpiCard icon={Wallet} label="Monthly Cost" value={inr(d.monthly_security_cost)} testid="kpi-cost" />
            <KpiCard icon={Receipt} label="Pending Invoices" value={d.pending_invoices} testid="kpi-pending" />
            <KpiCard icon={AlertTriangle} label="Open Incidents" value={d.open_incidents} testid="kpi-incidents" />
            <KpiCard icon={RefreshCw} label="Replacements" value={d.replacement_requests} testid="kpi-replacements" />
            <KpiCard icon={Receipt} label="Total Invoices" value={d.total_invoices} testid="kpi-invoices" />
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {quick.map(([label, Icon, to, accent]) => (
              <button key={label} onClick={() => navigate(to)} data-testid={`quick-${to.slice(1)}`}
                className={`card-lift rounded-xl p-5 text-left border ${accent ? "bg-brand text-white border-brand" : "bg-white border-navy-200 text-navy-900"}`}>
                <Icon size={22} className={accent ? "text-white" : "text-brand"} />
                <div className="mt-3 font-heading font-semibold">{label}</div>
              </button>
            ))}
          </div>

          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-bold text-navy-900">Active Contracts</h3>
              <Button variant="outline" className="border-navy-300" onClick={() => navigate("/contracts")}>View all</Button>
            </div>
            {d.contracts.length === 0 ? (
              <div className="bg-white border border-dashed border-navy-200 rounded-xl p-10 text-center text-navy-500">
                No contracts yet. <button className="text-brand font-medium" onClick={() => navigate("/post-requirement")}>Post a requirement</button> to get started.
              </div>
            ) : (
              <div className="bg-white border border-navy-200 rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-navy-50 text-navy-500 text-xs uppercase">
                    <tr><th className="text-left p-3">Agency</th><th className="text-left p-3">Location</th><th className="text-left p-3">Guards</th><th className="text-left p-3">Monthly</th><th className="text-left p-3">Status</th><th className="p-3"></th></tr>
                  </thead>
                  <tbody>
                    {d.contracts.map((c) => (
                      <tr key={c.id} className="border-t border-navy-100">
                        <td className="p-3 font-medium text-navy-900">{c.agency_name}</td>
                        <td className="p-3 text-navy-600">{c.service_location}</td>
                        <td className="p-3 text-navy-600">{c.num_guards}</td>
                        <td className="p-3 text-navy-900 font-semibold">{inr(c.monthly_charges)}</td>
                        <td className="p-3"><StatusPill status={c.status} /></td>
                        <td className="p-3 text-right"><Button size="sm" variant="ghost" onClick={() => navigate(`/contracts/${c.id}`)}><Eye size={15} /></Button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </DashboardShell>
  );
}
